import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { LATEST_TXN_DATES_QUERY, STORES_DETAIL_QUERY, TRANSACTIONS_QUERY } from '../graphql/queries';
import { useSelectedStore } from '../hooks/useSelectedStore';
import { eventFor, useCalendarEvents } from '../hooks/useCalendarEvents';
import { gql } from '../lib/api';
import { aggregateDrinks, type DrinkTransaction } from '../lib/drinkAnalytics';
import { airRegiAccountHour, airRegiBusinessDate, airRegiService, type AirRegiTransactionTime } from '../lib/airRegiBusinessTime';
import { type StoreLD } from '../lib/salesAnalytics';

interface Store extends StoreLD {
  name: string;
  airregiBusinessDayStart: string | null;
}

interface Transaction extends DrinkTransaction, AirRegiTransactionTime {
  total: number;
  customerCount: number | null;
}

type Segment = 'all' | 'lunch' | 'dinner';

interface DisplayOption {
  key: string;
  store: Store;
  segment: Segment;
  label: string;
}

interface DailyPoint {
  date: string;
  dateLabel: string;
  day: string;
  alcoholPerGuest: number | null;
  softPerGuest: number | null;
  customers: number;
  alcoholCount: number;
  softCount: number;
  unknownCount: number;
}

interface WeeklySeries {
  from: string;
  to: string;
  points: DailyPoint[];
  alcoholPerGuest: number | null;
  softPerGuest: number | null;
  customers: number;
  alcoholCount: number;
  softCount: number;
  unknownCount: number;
  weekday: string;
  eventTitle?: string;
}

const DAY_LABELS = ['火', '水', '木', '金', '土', '日'] as const;

const toDate = (date: string): Date => new Date(`${date}T00:00:00Z`);

const toDateString = (date: Date): string => date.toISOString().slice(0, 10);

const addDays = (date: string, amount: number): string => {
  const next = toDate(date);
  next.setUTCDate(next.getUTCDate() + amount);
  return toDateString(next);
};

const todayInJapan = (): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
};

const businessWeekRange = (latestDate: string): { from: string; to: string } => {
  const latest = toDate(latestDate);
  const daysFromTuesday = (latest.getUTCDay() + 5) % 7;
  const from = addDays(latestDate, -daysFromTuesday);
  return { from, to: addDays(from, 5) };
};

const shortDate = (date: string): string => {
  const [, month, day] = date.split('-');
  return `${month}/${day}`;
};

const longDate = (date: string): string => {
  const [year, month, day] = date.split('-');
  return `${year}年${month}月${day}日`;
};

const fmtRate = (value: number | null): string => value == null ? 'データなし' : `${value.toFixed(2)}杯/人`;

const isDateString = (value: string | null): value is string => /^\d{4}-\d{2}-\d{2}$/.test(value ?? '');

const displayOptionsFor = (stores: Store[]): DisplayOption[] =>
  [...stores]
    .sort((a, b) => Number(!!b.lunchStart) - Number(!!a.lunchStart))
    .flatMap<DisplayOption>((store): DisplayOption[] => {
      if (store.lunchStart && store.lunchEnd) {
        return [
          { key: `${store.id}:lunch`, store, segment: 'lunch', label: `${store.name}（ランチ）` },
          { key: `${store.id}:dinner`, store, segment: 'dinner', label: `${store.name}（ディナー）` },
        ];
      }
      return [{ key: `${store.id}:all`, store, segment: 'all', label: store.name }];
    });

export const DrinkAnalysisPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedStoreId, setStoredStoreId] = useSelectedStore();
  const initialStoreId = searchParams.get('store') || storedStoreId;
  const initialSegment = searchParams.get('segment');
  const initialDate = searchParams.get('date');
  const [selectedKey, setSelectedKey] = useState(
    `${initialStoreId}:${initialSegment === 'lunch' || initialSegment === 'dinner' ? initialSegment : 'all'}`,
  );
  const [selectedDate, setSelectedDate] = useState(isDateString(initialDate) ? initialDate : '');

  const storesQ = useQuery({
    queryKey: ['storesDetail'],
    queryFn: () => gql<{ stores: Store[] }>(STORES_DETAIL_QUERY),
  });
  const stores = useMemo(() => storesQ.data?.stores ?? [], [storesQ.data?.stores]);
  const displayOptions = useMemo(() => displayOptionsFor(stores), [stores]);
  const requestedStoreId = selectedKey.slice(0, selectedKey.lastIndexOf(':'));
  const selectedOption = displayOptions.find((option) => option.key === selectedKey)
    ?? displayOptions.find((option) => option.store.id === requestedStoreId)
    ?? displayOptions[0];
  const effectiveStoreId = selectedOption?.store.id ?? '';
  const effectiveSegment = selectedOption?.segment ?? 'all';

  useEffect(() => {
    if (!selectedOption) return;
    if (selectedKey !== selectedOption.key) setSelectedKey(selectedOption.key);
    setStoredStoreId(effectiveStoreId);
  }, [effectiveStoreId, selectedKey, selectedOption, setStoredStoreId]);

  useEffect(() => {
    if (!effectiveStoreId) return;
    const next = new URLSearchParams(searchParams);
    next.set('store', effectiveStoreId);
    if (effectiveSegment === 'all') next.delete('segment');
    else next.set('segment', effectiveSegment);
    if (selectedDate) next.set('date', selectedDate);
    else next.delete('date');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [effectiveSegment, effectiveStoreId, searchParams, selectedDate, setSearchParams]);

  const probeRange = useMemo(() => {
    const to = todayInJapan();
    return { from: addDays(to, -60), to };
  }, []);

  const latestQ = useQuery({
    enabled: !!effectiveStoreId,
    queryKey: ['drinkLatest', effectiveStoreId, effectiveSegment, stores, probeRange.to],
    queryFn: async () => {
      for (let offset = 0; offset <= 60; offset += 7) {
        const results = await Promise.all(Array.from({ length: Math.min(7, 61 - offset) }, (_, i) => {
          const date = addDays(probeRange.to, -offset - i);
          return gql<{ transactions: AirRegiTransactionTime[] }>(LATEST_TXN_DATES_QUERY, {
            storeId: effectiveStoreId, from: date, to: date,
          });
        }));
        const store = stores.find((item) => item.id === effectiveStoreId);
        const transactions = results.flatMap((r) => r.transactions).filter((tx) =>
          effectiveSegment === 'all' || airRegiService(tx, store) === effectiveSegment);
        if (transactions.length) return { transactions };
      }
      return { transactions: [] };
    },
  });

  const latestDate = useMemo(() => {
    const store = stores.find((item) => item.id === effectiveStoreId);
    const dates = latestQ.data?.transactions
      .filter((transaction) =>
        effectiveSegment === 'all' || airRegiService(transaction, store) === effectiveSegment)
      .map((transaction) => airRegiBusinessDate(transaction, store?.airregiBusinessDayStart)) ?? [];
    return dates.sort().at(-1) ?? '';
  }, [effectiveSegment, effectiveStoreId, latestQ.data, stores]);
  const latestCompleteDate = useMemo(() => {
    if (!latestDate) return '';
    const range = businessWeekRange(latestDate);
    return range.to;
  }, [latestDate]);

  useEffect(() => {
    if (!selectedDate && latestCompleteDate) setSelectedDate(latestCompleteDate);
  }, [latestCompleteDate, selectedDate]);

  const selectedWeekRange = useMemo(
    () => (selectedDate ? businessWeekRange(selectedDate) : null),
    [selectedDate],
  );
  const sixWeekRange = useMemo(
    () => selectedWeekRange
      ? { from: selectedWeekRange.from, to: selectedWeekRange.to }
      : null,
    [selectedWeekRange],
  );
  const eventsQ = useCalendarEvents(selectedWeekRange?.from, selectedWeekRange?.to);

  const transactionsQ = useQuery({
    enabled: !!effectiveStoreId && !!sixWeekRange,
    queryKey: ['drinkKpi', effectiveStoreId, sixWeekRange?.from, sixWeekRange?.to],
    queryFn: async () => {
      // DynamoDBは会計日で保存するため、日曜営業分の月曜会計まで取得する。
      const days = await Promise.all(Array.from({ length: 7 }, (_, i) => {
        const date = addDays(sixWeekRange!.from, i);
        return gql<{ transactions: Transaction[] }>(TRANSACTIONS_QUERY, {
          storeId: effectiveStoreId, from: date, to: date,
        });
      }));
      return { transactions: days.flatMap((day) => day.transactions) };
    },
  });

  const weeklySeries = useMemo<WeeklySeries[]>(() => {
    if (!selectedWeekRange) return [];
    const store = stores.find((s) => s.id === effectiveStoreId);
    const matching = (transactionsQ.data?.transactions ?? []).filter((tx) =>
      effectiveSegment === 'all' || airRegiService(tx, store) === effectiveSegment);
    const startTime = effectiveSegment === 'lunch' ? store?.lunchStart : store?.dinnerStart;
    const endTime = effectiveSegment === 'lunch' ? store?.lunchEnd : store?.dinnerEnd;
    const startHour = startTime ? Number(startTime.split(':')[0]) : 0;
    const endHour = endTime ? Math.ceil(Number(endTime.split(':')[0]) + Number(endTime.split(':')[1]) / 60) : 24;
    const plottedHours = matching
      .filter((tx) => {
        const date = airRegiBusinessDate(tx, store?.airregiBusinessDayStart);
        return date >= selectedWeekRange.from && date <= selectedWeekRange.to;
      })
      .map((tx) => airRegiAccountHour(tx, store?.airregiBusinessDayStart))
      .filter((hour): hour is number => hour !== null);
    const firstHour = Math.min(startHour, ...plottedHours);
    const lastHour = Math.max(endHour - 1, ...plottedHours) + 2;
    return DAY_LABELS.map((weekday, dayIndex) => {
      const date = addDays(selectedWeekRange.from, dayIndex);
      const event = eventFor(eventsQ.data?.calendarEvents, effectiveStoreId, date);
      const daily = event ? [] : matching.filter((tx) =>
        airRegiBusinessDate(tx, store?.airregiBusinessDayStart) === date);
      const points = Array.from({ length: lastHour - firstHour + 1 }, (_, i) => {
        const hour = firstHour + i;
        return { date, dateLabel: shortDate(date), day: `${String(hour).padStart(2, '0')}:00`,
          ...aggregateDrinks(daily.filter((tx) => airRegiAccountHour(tx, store?.airregiBusinessDayStart) === hour)) };
      });
      return { from: date, to: date, weekday, points, eventTitle: event?.title, ...aggregateDrinks(daily) };
    });
  }, [effectiveSegment, effectiveStoreId, eventsQ.data, selectedWeekRange, stores, transactionsQ.data]);

  const validDays = weeklySeries.filter((day) => day.alcoholPerGuest != null);
  const totalCustomers = validDays.reduce((sum, day) => sum + day.customers, 0);
  const latestWeek = weeklySeries.length ? {
    alcoholPerGuest: totalCustomers ? validDays.reduce((sum, day) => sum + day.alcoholCount, 0) / totalCustomers : null,
    softPerGuest: totalCustomers ? validDays.reduce((sum, day) => sum + day.softCount, 0) / totalCustomers : null,
    points: weeklySeries.map((day) => ({ ...day, date: day.from, dateLabel: shortDate(day.from), day: day.weekday })),
  } : undefined;
  const rateAxisMax = Math.max(1, Math.ceil(Math.max(0, ...weeklySeries.flatMap((week) =>
    week.points.flatMap((point) => [point.alcoholPerGuest ?? 0, point.softPerGuest ?? 0]))) * 2) / 2);
  const isLoading = storesQ.isLoading || latestQ.isLoading || transactionsQ.isLoading || eventsQ.isLoading;
  const hasError = storesQ.isError || latestQ.isError || transactionsQ.isError || eventsQ.isError;

  return (
    <div className="kpi-weekly-page">
      <header className="kpi-weekly-heading">
        <div>
          <div className="kpi-weekly-kicker">KPI LAB / DRINKS PER GUEST</div>
          <h1>ドリンク分析</h1>
          <p>来客1人あたりのアルコール・ソフトドリンク注文数を、火〜日の1日1グラフで時間別に比較します。</p>
        </div>
        <label className="kpi-weekly-store">
          <span>表示店舗・時間帯</span>
          <select value={selectedOption?.key ?? ''} onChange={(event) => setSelectedKey(event.target.value)}>
            {displayOptions.map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>
        </label>
      </header>

      <div className="kpi-weekly-note">
        <span>KPI検討用</span>
        <p>Airレジの来店営業日と店舗マスタの集計終点でランチ／ディナーを分け、会計時刻の1時間ごとに注文数量 ÷ 来客数を表示します。横軸は最終会計から2時間先まで表示します。ノンアルコールとカフェはソフトドリンクに含みます。人数が不足する日・未取込日はデータなし、イベント日は除外します。</p>
      </div>

      <div className="kpi-weekly-range-controls">
        <label>
          <span>表示する週の基準日</span>
          <input
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
          />
        </label>
        <div className="kpi-weekly-range-actions">
          <button type="button" disabled={!selectedDate} onClick={() => setSelectedDate(addDays(selectedDate, -7))}>← 前の週</button>
          <button type="button" disabled={!selectedDate} onClick={() => setSelectedDate(addDays(selectedDate, 7))}>次の週 →</button>
          <button type="button" disabled={!latestCompleteDate} onClick={() => setSelectedDate(latestCompleteDate)}>最新週</button>
        </div>

      </div>

      {hasError && (
        <section className="kpi-weekly-state" role="alert">
          データを取得できませんでした。時間をおいて再度お試しください。
        </section>
      )}

      {!hasError && isLoading && (
        <section className="kpi-weekly-state">週間データを読み込んでいます…</section>
      )}

      {!hasError && !isLoading && !latestDate && !selectedDate && (
        <section className="kpi-weekly-state">この店舗の直近60日間に売上データがありません。</section>
      )}

      {!hasError && !isLoading && selectedWeekRange && latestWeek && (
        <>
          <section className="kpi-weekly-overview">
            <div className="kpi-weekly-period">
              <span>対象営業週</span>
              <strong>{longDate(selectedWeekRange.from)} — {longDate(selectedWeekRange.to)}</strong>
              <small>{selectedOption?.label} ／ データ最新日 {latestDate ? longDate(latestDate) : '直近60日間なし'}</small>
            </div>
            <div className="kpi-weekly-total kpi-weekly-total-sales">
              <span>アルコール / 人</span>
              <strong>{fmtRate(latestWeek.alcoholPerGuest)}</strong>
              <small>集計可能日の合計杯数 ÷ 合計来客数</small>
            </div>
            <div className="kpi-weekly-total kpi-weekly-total-customers">
              <span>ソフトドリンク / 人</span>
              <strong>{fmtRate(latestWeek.softPerGuest)}</strong>
              <small>集計可能日の合計杯数 ÷ 合計来客数</small>
            </div>
          </section>

          <section className="kpi-weekly-chart-panel">
            <div className="kpi-weekly-chart-heading">
              <div>
                <span>TUESDAY — SUNDAY</span>
                <h2>曜日別・時間別の1人あたりドリンク注文数</h2>
              </div>
              <p>月曜日は定休日のため表示していません</p>
            </div>
            <div className="kpi-weekly-legend" aria-label="グラフの凡例">
              <span><i className="kpi-weekly-legend-sales" />アルコール</span>
              <span><i className="kpi-weekly-legend-customers" />ソフトドリンク</span>
            </div>

            <div className="kpi-weekly-chart-grid">
              {weeklySeries.map((week) => (
                <article className="kpi-weekly-chart-card" key={week.from}>
                  <header>
                    <div>
                      <span>{week.weekday}曜日</span>
                      <h3>{longDate(week.from)}（{week.weekday}）</h3>
                    </div>
                  </header>
                  {week.eventTitle && <div className="kpi-event-excluded">{week.eventTitle}・KPI集計対象外<small>元データは保持されています</small></div>}
                  {!week.eventTitle && <>
                  <div className="kpi-weekly-card-totals">
                    <span>アルコール {fmtRate(week.alcoholPerGuest)}</span>
                    <span>ソフトドリンク {fmtRate(week.softPerGuest)}</span>
                  </div>
                  <div className="kpi-weekly-chart" aria-label={`${shortDate(week.from)}から${shortDate(week.to)}までの1人あたりドリンク杯数の折れ線グラフ`}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={week.points} margin={{ top: 12, right: 2, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="2 5" vertical={false} />
                        <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#65605d', fontSize: 11 }} />
                        <YAxis
                          yAxisId="sales"
                          domain={[0, rateAxisMax]}
                          axisLine={false}
                          tickLine={false}
                          width={52}
                          tickCount={4}
                          tick={{ fill: '#65605d', fontSize: 9 }}
                          tickFormatter={(value) => `${Number(value).toFixed(1)}杯`}
                        />
                        <Tooltip
                          labelFormatter={(label, payload) => {
                            const point = payload?.[0]?.payload as DailyPoint | undefined;
                            return point ? `${longDate(point.date)}（${label}）` : String(label);
                          }}
                          formatter={(value, name) => [
                            fmtRate(Number(value)),
                            name,
                          ]}
                        />
                        <Line
                          yAxisId="sales"
                          type="monotone"
                          dataKey="alcoholPerGuest"
                          name="アルコール"
                          stroke="#0088b0"
                          strokeWidth={2.5}
                          dot={{ r: 3, fill: '#faf9f8', strokeWidth: 2 }}
                          activeDot={{ r: 5 }}
                        />
                        <Line
                          yAxisId="sales"
                          type="monotone"
                          dataKey="softPerGuest"
                          name="ソフトドリンク"
                          stroke="#d6006c"
                          strokeWidth={2.5}
                          dot={{ r: 3, fill: '#faf9f8', strokeWidth: 2 }}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="kpi-drink-status">{week.points.filter((p) => p.alcoholPerGuest == null).map((p) => p.day).join('・') || '全時間帯集計可能'}{week.points.some((p) => p.alcoholPerGuest == null) ? '：データなし（未取込または人数不足）' : ''}</p>
                  {week.points.some((p) => p.unknownCount > 0) && <p className="kpi-drink-status">分類不明ドリンク：{week.unknownCount}点（集計対象外）</p>}
                  </>}
                </article>
              ))}
            </div>

            <h3 className="kpi-weekly-days-title">選択週の日別実績</h3>
            <div className="kpi-weekly-days" aria-label="選択週の日別実績">
              {latestWeek.points.map((point) => (
                <article key={point.date}>
                  <div><strong>{point.day}</strong><span>{point.dateLabel}</span></div>
                  {point.eventTitle ? <p className="kpi-event-badge">{point.eventTitle}・KPI除外</p> : <dl>
                    <div><dt>アルコール / 人</dt><dd>{fmtRate(point.alcoholPerGuest)}</dd></div>
                    <div><dt>ソフトドリンク / 人</dt><dd>{fmtRate(point.softPerGuest)}</dd></div>
                  </dl>}
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};
